import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Wand2, Loader2, History, ChevronDown, ChevronRight, RotateCcw,
} from "lucide-react";
import { api } from "@/lib/api";

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

function VersionRow({ version, articleId, disabled }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const restore = useMutation({
    mutationFn: () => api.restoreArticleVersion(articleId, version.version),
    onSuccess: () => {
      toast.success(`Restored version ${version.version}`);
      qc.invalidateQueries({ queryKey: ["article", articleId] });
    },
    onError: (e) => toast.error(formatError(e?.response?.data?.detail) || e.message),
  });

  const created = version.created_at
    ? new Date(version.created_at).toLocaleString()
    : "";
  const label =
    version.kind === "llm_revision"
      ? "LLM revision"
      : version.kind === "restore"
        ? "Restore snapshot"
        : version.kind === "fact_check_fix"
          ? "Before fact-check fix"
          : version.kind === "fact_check_fix_undo"
            ? "Before undoing a fact-check fix"
            : "Snapshot";

  return (
    <div
      data-testid={`version-row-${version.version}`}
      className="border-b border-[var(--border)] last:border-b-0"
    >
      <div className="flex items-start gap-2 px-4 py-3">
        <button
          onClick={() => setOpen((v) => !v)}
          className="mt-0.5 text-[var(--ink-muted)] hover:text-[var(--ink)] shrink-0"
          aria-label="expand version"
        >
          {open ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono-display text-[var(--ink-muted)]">
              v{version.version}
            </span>
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-[var(--secondary)] text-[var(--ink)] font-mono-display">
              {label}
            </span>
            <span className="text-xs text-[var(--ink-muted)]">{created}</span>
          </div>
          {version.revision_prompt && (
            <div className="mt-1 text-sm text-[var(--ink)] leading-snug">
              <span className="italic">&ldquo;{version.revision_prompt}&rdquo;</span>
            </div>
          )}
          <div className="text-xs text-[var(--ink-muted)] mt-1 truncate">
            <span className="font-medium text-[var(--ink)]">{version.headline}</span>
          </div>

          {open && (
            <div className="mt-3 space-y-3">
              {version.dek && (
                <div className="text-sm italic text-[var(--ink-muted)]">
                  {version.dek}
                </div>
              )}
              <div
                className="prose-editorial text-sm bg-[var(--bg)] p-3 border border-[var(--border)] max-h-80 overflow-auto"
                dangerouslySetInnerHTML={{ __html: version.body_html || "" }}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                {version.excerpt && (
                  <div>
                    <div className="overline mb-1">Excerpt</div>
                    <div className="text-[var(--ink)]">{version.excerpt}</div>
                  </div>
                )}
                {version.facebook_post && (
                  <div>
                    <div className="overline mb-1">Facebook</div>
                    <div className="text-[var(--ink)] whitespace-pre-wrap">
                      {version.facebook_post}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <button
          data-testid={`restore-version-${version.version}-btn`}
          onClick={() => restore.mutate()}
          disabled={disabled || restore.isPending}
          className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-50 disabled:cursor-not-allowed"
          title="Restore this version (current state is saved first)"
        >
          {restore.isPending ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <RotateCcw className="w-3 h-3" />
          )}
          Restore
        </button>
      </div>
    </div>
  );
}

export default function ReviseArticlePanel({ article, disabled }) {
  const qc = useQueryClient();
  const [prompt, setPrompt] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);

  const revise = useMutation({
    mutationFn: () => api.reviseArticle(article.id, prompt.trim()),
    onSuccess: () => {
      toast.success("Article revised — previous version saved to history");
      setPrompt("");
      qc.invalidateQueries({ queryKey: ["article", article.id] });
    },
    onError: (e) =>
      toast.error(formatError(e?.response?.data?.detail) || e.message),
  });

  const versions = Array.isArray(article.versions) ? article.versions : [];
  const canSubmit = prompt.trim().length >= 3 && !revise.isPending && !disabled;

  return (
    <section
      data-testid="revise-panel"
      className="mb-12 bg-white border border-[var(--border)]"
    >
      <div className="px-5 py-3 border-b border-[var(--border)] flex items-center gap-2">
        <Wand2 className="w-3.5 h-3.5 text-[var(--accent)]" />
        <span className="overline">Revise with AI</span>
      </div>

      <div className="px-5 py-4 space-y-3">
        <p className="text-xs text-[var(--ink-muted)] leading-relaxed">
          Ask the writer to update the article. The current article plus your
          instructions are sent to the LLM; the previous version is saved to
          history automatically.
        </p>
        <textarea
          data-testid="revise-prompt-input"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          disabled={disabled || revise.isPending}
          rows={4}
          maxLength={3000}
          placeholder="e.g. Please add a paragraph that mentions last year's winner of the race was Juanito Perez."
          className="w-full text-sm bg-white border border-[var(--border)] px-3 py-2 focus:outline-none focus:border-[var(--slate-900)] resize-y disabled:opacity-60"
        />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] text-[var(--ink-muted)] font-mono-display">
            {prompt.trim().length}/3000
          </div>
          <button
            data-testid="revise-submit-btn"
            data-ai="true"
            onClick={() => revise.mutate()}
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {revise.isPending ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Revising… (30–60s)
              </>
            ) : (
              <>
                <Wand2 className="w-3.5 h-3.5" />
                Send to LLM
              </>
            )}
          </button>
        </div>
      </div>

      {/* Version history */}
      <div className="border-t border-[var(--border)]">
        <button
          data-testid="version-history-toggle"
          onClick={() => setHistoryOpen((v) => !v)}
          className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-[var(--secondary)]"
        >
          <div className="flex items-center gap-2">
            <History className="w-3.5 h-3.5 text-[var(--ink-muted)]" />
            <span className="overline">
              Version history · {versions.length}
            </span>
          </div>
          {historyOpen ? (
            <ChevronDown className="w-4 h-4 text-[var(--ink-muted)]" />
          ) : (
            <ChevronRight className="w-4 h-4 text-[var(--ink-muted)]" />
          )}
        </button>
        {historyOpen && (
          <div
            data-testid="version-history-list"
            className="border-t border-[var(--border)]"
          >
            {versions.length === 0 ? (
              <div className="px-5 py-4 text-xs text-[var(--ink-muted)] italic">
                No previous versions yet — every revision saves the last state
                here.
              </div>
            ) : (
              [...versions].reverse().map((v) => (
                <VersionRow
                  key={v.version}
                  version={v}
                  articleId={article.id}
                  disabled={revise.isPending}
                />
              ))
            )}
          </div>
        )}
      </div>
    </section>
  );
}
